/**
 * cart-drawer.js — Drinkin Theme
 *
 * Responsabilidades:
 * - Abrir/fechar drawer
 * - Buscar estado do carrinho via /cart.js
 * - Renderizar itens dinamicamente
 * - Add to cart via /cart/add.js (sem reload)
 * - Update quantidade via /cart/change.js
 * - Remover item via /cart/change.js (qty=0)
 * - Atualizar todas as bubbles/contadores da página
 * - Barra de frete grátis dinâmica
 *
 * Incluir no layout/theme.liquid:
 * {{ 'cart-drawer.js' | asset_url | script_tag }}
 * (ou adicionar ao bundle do theme.js)
 */

(function () {
  'use strict';

  /* ── Configuração ────────────────────────────────────────── */
  const CONFIG = {
    freeShipThreshold: 15000, // em centavos (R$ 150,00) — ajuste conforme necessário
    drawerSelector:    '#cart-drawer',
    overlaySelector:   '#cart-overlay',
    countSelector:     '.cart-bubble, .cart-drawer__count',
  };

  /* ── Referências DOM ─────────────────────────────────────── */
  const drawer     = document.getElementById('cart-drawer');
  const overlay    = document.getElementById('cart-overlay');
  const closeBtn   = document.getElementById('cart-drawer-close');
  const itemsEl    = document.getElementById('drawer-items');
  const footerEl   = document.getElementById('drawer-footer');
  const freeShipEl = document.getElementById('drawer-free-ship');
  const loadingEl  = document.getElementById('drawer-loading');
  const countEl    = document.getElementById('drawer-count');

  if (!drawer) return; // Drawer não está na página

  /* ── Utilitários ─────────────────────────────────────────── */
  function formatMoney(cents) {
    return 'R$\u00a0' + (cents / 100)
      .toFixed(2)
      .replace('.', ',')
      .replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  }

  function updateAllCounters(count) {
    // Bubble do header
    document.querySelectorAll('.cart-bubble').forEach(el => {
      el.textContent   = count;
      el.style.display = count > 0 ? 'flex' : 'none';
    });
    // Contador do drawer header
    if (countEl) {
      countEl.textContent = count > 0 ? `(${count})` : '';
    }
  }

  function setLoading(state) {
    if (loadingEl) loadingEl.style.display = state ? 'flex' : 'none';
    if (itemsEl)   itemsEl.style.opacity   = state ? '0.4' : '1';
  }

  /* ── Fetch carrinho ──────────────────────────────────────── */
  async function fetchCart() {
    const res  = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
    return res.json();
  }

  async function cartAdd(variantId, quantity = 1, properties = {}) {
    const res = await fetch('/cart/add.js', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body:    JSON.stringify({ id: variantId, quantity, properties }),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.description || 'Erro ao adicionar produto.');
    }
    return res.json();
  }

  async function cartChange(key, quantity) {
    const res = await fetch('/cart/change.js', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body:    JSON.stringify({ id: key, quantity }),
    });
    return res.json();
  }

  /* ── Render ──────────────────────────────────────────────── */
  function renderFreeShip(totalCents) {
    if (!freeShipEl) return;
    const threshold = CONFIG.freeShipThreshold;
    const remaining = threshold - totalCents;
    freeShipEl.style.display = 'block';

    if (remaining <= 0) {
      freeShipEl.innerHTML = `
        <div class="free-ship-bar free-ship-bar--done">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>
          <span>Você ganhou <strong>frete grátis!</strong></span>
        </div>`;
    } else {
      const pct = Math.round((totalCents / threshold) * 100);
      freeShipEl.innerHTML = `
        <div class="free-ship-bar">
          <span>Faltam <strong>${formatMoney(remaining)}</strong> para frete grátis</span>
          <div class="free-ship-bar__track">
            <div class="free-ship-bar__fill" style="width:${pct}%"></div>
          </div>
        </div>`;
    }
  }

  function renderItems(cart) {
    if (!itemsEl) return;

    if (cart.item_count === 0) {
      itemsEl.innerHTML = `
        <div class="cart-drawer__empty">
          <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="#ddd" stroke-width="1.5">
            <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
            <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
          </svg>
          <p>Seu carrinho está vazio</p>
          <a href="/collections/all" class="btn btn--primary btn--sm" style="margin-top:12px;">
            Ver produtos
          </a>
        </div>`;
      return;
    }

    itemsEl.innerHTML = cart.items.map(item => {
      const imgSrc  = item.image
        ? item.image.replace(/(\.[^.]+)$/, '_120x120$1')
        : '';
      const imgHtml = imgSrc
        ? `<img src="${imgSrc}" alt="${escHtml(item.product_title)}" class="cart-item-mini__img" loading="lazy" width="60" height="60">`
        : `<div class="cart-item-mini__img cart-item-mini__img--placeholder"></div>`;

      const variantHtml = (item.variant_title && item.variant_title !== 'Default Title')
        ? `<span class="cart-item-mini__variant">${escHtml(item.variant_title)}</span>`
        : '';

      return `
        <div class="cart-item-mini" data-key="${item.key}">
          <a href="${item.url}" class="cart-item-mini__img-wrap" tabindex="-1">
            ${imgHtml}
          </a>
          <div class="cart-item-mini__info">
            <a href="${item.url}" class="cart-item-mini__name">${escHtml(item.product_title)}</a>
            ${variantHtml}
            <div class="cart-item-mini__bottom">
              <div class="cart-item-mini__qty-wrap">
                <button class="cart-item-mini__qty-btn" data-key="${item.key}" data-action="minus" aria-label="Diminuir">−</button>
                <span class="cart-item-mini__qty-num">${item.quantity}</span>
                <button class="cart-item-mini__qty-btn" data-key="${item.key}" data-action="plus" aria-label="Aumentar">+</button>
              </div>
              <span class="cart-item-mini__price">${formatMoney(item.final_line_price)}</span>
            </div>
          </div>
          <button class="cart-item-mini__remove" data-key="${item.key}" aria-label="Remover ${escHtml(item.product_title)}">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>`;
    }).join('');

    // Bind events nos itens recém-renderizados
    bindItemEvents();
  }

  function renderFooter(cart) {
    if (!footerEl) return;

    if (cart.item_count === 0) {
      footerEl.style.display = 'none';
      return;
    }

    footerEl.style.display = 'block';
    footerEl.innerHTML = `
      <div class="cart-drawer__total">
        <span>Subtotal</span>
        <span class="cart-drawer__total-price">${formatMoney(cart.total_price)}</span>
      </div>
      <div class="cart-drawer__footer-actions">
        <a href="/cart" class="btn btn--outline btn--full">Ver carrinho</a>
        <a href="/checkout" class="btn btn--primary btn--full">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
          </svg>
          Finalizar compra
        </a>
      </div>
      <div class="cart-summary-box__payments" style="justify-content:center;margin-top:10px;">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
        </svg>
        <span style="font-size:11px;color:#999;">Pagamento 100% seguro</span>
      </div>`;
  }

  function renderCart(cart) {
    updateAllCounters(cart.item_count);
    renderFreeShip(cart.total_price);
    renderItems(cart);
    renderFooter(cart);
  }

  /* ── Escape HTML ─────────────────────────────────────────── */
  function escHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /* ── Bind events nos itens (após re-render) ──────────────── */
  function bindItemEvents() {
    // Botões qty
    itemsEl.querySelectorAll('.cart-item-mini__qty-btn').forEach(btn => {
      btn.addEventListener('click', async () => {
        const key     = btn.dataset.key;
        const action  = btn.dataset.action;
        const qtyEl   = btn.closest('.cart-item-mini__qty-wrap').querySelector('.cart-item-mini__qty-num');
        const current = parseInt(qtyEl.textContent, 10);
        const newQty  = action === 'plus' ? current + 1 : Math.max(0, current - 1);

        setLoading(true);
        try {
          const cart = await cartChange(key, newQty);
          renderCart(cart);
        } finally {
          setLoading(false);
        }
      });
    });

    // Botões remover
    itemsEl.querySelectorAll('.cart-item-mini__remove').forEach(btn => {
      btn.addEventListener('click', async () => {
        const key = btn.dataset.key;
        // Animação de saída
        const row = btn.closest('.cart-item-mini');
        if (row) {
          row.style.transition = 'opacity .25s, transform .25s';
          row.style.opacity    = '0';
          row.style.transform  = 'translateX(16px)';
          await new Promise(r => setTimeout(r, 260));
        }
        setLoading(true);
        try {
          const cart = await cartChange(key, 0);
          renderCart(cart);
        } finally {
          setLoading(false);
        }
      });
    });
  }

  /* ── Abrir/fechar drawer ─────────────────────────────────── */
  function openDrawer() {
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    if (overlay) {
      overlay.style.display = 'block';
      requestAnimationFrame(() => overlay.classList.add('is-visible'));
    }
    document.body.style.overflow = 'hidden';
    closeBtn && closeBtn.focus();
  }

  function closeDrawer() {
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    if (overlay) {
      overlay.classList.remove('is-visible');
      overlay.addEventListener('transitionend', () => {
        overlay.style.display = '';
      }, { once: true });
    }
    document.body.style.overflow = '';
  }

  /* ── Add to cart público (chamado pelos cards) ───────────── */
  window.DrinkinCart = {

    /**
     * Adiciona um produto e abre o drawer
     * @param {number} variantId
     * @param {number} quantity
     * @param {HTMLElement} triggerBtn — botão que disparou (para feedback)
     */
    async addAndOpen(variantId, quantity = 1, triggerBtn = null) {
      if (triggerBtn) {
        triggerBtn.disabled    = true;
        triggerBtn.dataset.origText = triggerBtn.innerHTML;
        triggerBtn.innerHTML   = '<svg class="spin" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0"/></svg>';
      }
      try {
        await cartAdd(variantId, quantity);
        const cart = await fetchCart();
        renderCart(cart);
        openDrawer();
      } catch (err) {
        alert(err.message || 'Não foi possível adicionar o produto.');
      } finally {
        if (triggerBtn) {
          triggerBtn.disabled  = false;
          triggerBtn.innerHTML = triggerBtn.dataset.origText || '+ Adicionar';
        }
      }
    },

    open:  openDrawer,
    close: closeDrawer,
    refresh: async function () {
      const cart = await fetchCart();
      renderCart(cart);
    },
  };

  /* ── Eventos de UI ───────────────────────────────────────── */
  if (closeBtn) closeBtn.addEventListener('click', closeDrawer);
  if (overlay)  overlay.addEventListener('click', closeDrawer);
  document.addEventListener('keydown', e => {
    if (e.key === 'Escape' && drawer.classList.contains('is-open')) closeDrawer();
  });

  /* ── Delegação: botões "+ Adicionar" nos cards ───────────── */
  document.addEventListener('click', async e => {
    const btn = e.target.closest('[data-add-cart]');
    if (!btn) return;
    e.preventDefault();

    const form      = btn.closest('form');
    const variantId = form
      ? parseInt(form.querySelector('[name="id"]')?.value, 10)
      : parseInt(btn.dataset.variantId, 10);
    const quantity  = form
      ? parseInt(form.querySelector('[name="quantity"]')?.value || '1', 10)
      : 1;

    if (!variantId) return;
    await window.DrinkinCart.addAndOpen(variantId, quantity, btn);
  });

  /* ── Init: carrega estado atual do carrinho ──────────────── */
  fetchCart().then(renderCart).catch(console.error);

})();